import { type KeyboardEvent, type MutableRefObject, useCallback } from 'react'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
} from '@/components/app/panels/left-panel/type-panel/type-panel-presets'
import { getShortcutPlatform } from '@/lib/keyboard/keyboard-shortcuts'
import { resolveShiftNavigation } from '@/lib/shapes/shape-text-navigation'
import { matchesTextEditorShortcut } from '@/lib/keyboard/text-editor-shortcuts'
import { isEditorZoomModifierShortcut } from '@/lib/zoom/editor-zoom-keyboard'
import { resolveBoldItalicToggle } from '../helpers/bold-italic-toggle'
import {
  CARET_NAVIGATION_KEYS,
  chainReplacementForKey,
  isModLetter,
  isUndoRedoCombo,
  shiftNavLeavesFrame,
} from '../helpers/keyboard-routing'
import type { ShapeTextEditorProps } from '../types/shape-text-editor-props'
import type {
  ShapeTextCarets,
  ShapeTextCharRunMap,
  ShapeTextFonts,
} from '../types/shape-text-layout-state'
import type { ShapeTextParagraphsChangeOptions } from './use-shape-text-editing'

type ChainKeyboardProps = Pick<
  ShapeTextEditorProps,
  | 'chainFrameIds'
  | 'onChainSelectionChange'
  | 'onChainSelectionReplace'
  | 'onChainSelectionExtend'
  | 'onChainBackspaceAtStart'
  | 'onChainArrowLeftAtStart'
  | 'onChainArrowRightAtEnd'
  | 'onExitEdit'
  | 'onSelectChain'
  | 'onChainClipboard'
>

/**
 * The hidden textarea's `onKeyDown`: one ordered decision tree. The ORDER of the branches is the
 * behavior — undo/redo passes through first, then font-size stepping, cross-frame replacement,
 * shifted navigation across a link boundary, selection collapse, the chain edge hand-offs, and
 * finally the editor shortcuts.
 */
export function useShapeTextKeyboard({
  shapeId,
  carets,
  hasChainSelection,
  hasNextInChain,
  hasPrevInChain,
  textareaRef,
  charRunMapRef,
  fontsRef,
  getEditingStyle,
  applyRunStyle,
  markPointSizeBoundaryHit,
  execSelectAll,
  execCopy,
  execCut,
  chainFrameIds,
  onChainSelectionChange,
  onChainSelectionReplace,
  onChainSelectionExtend,
  onChainBackspaceAtStart,
  onChainArrowLeftAtStart,
  onChainArrowRightAtEnd,
  onExitEdit,
  onSelectChain,
  onChainClipboard,
}: ChainKeyboardProps & {
  shapeId: string
  carets: ShapeTextCarets
  hasChainSelection: boolean
  hasNextInChain: boolean
  hasPrevInChain: boolean
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  charRunMapRef: MutableRefObject<ShapeTextCharRunMap>
  fontsRef: MutableRefObject<ShapeTextFonts>
  getEditingStyle: () => TextRunStyleUpdate | null
  applyRunStyle: (update: TextRunStyleUpdate, options?: ShapeTextParagraphsChangeOptions) => void
  markPointSizeBoundaryHit: () => void
  execSelectAll: () => void
  execCopy: () => void
  execCut: () => void
}) {
  // Ctrl/Cmd+B (bold) and Ctrl/Cmd+I (italic) — see `resolveBoldItalicToggle`.
  const toggleBoldOrItalic = useCallback(
    (kind: 'bold' | 'italic') => {
      const ta = textareaRef.current
      const fonts = fontsRef.current

      if (!ta || !fonts) return

      const selStart = ta.selectionStart ?? 0
      const update = resolveBoldItalicToggle(kind, {
        selStart,
        selEnd: ta.selectionEnd ?? selStart,
        charRunMap: charRunMapRef.current,
        fonts,
        getEditingStyle,
      })

      if (update) applyRunStyle(update)
    },
    [applyRunStyle, charRunMapRef, fontsRef, getEditingStyle, textareaRef]
  )

  return useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Let Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y (redo) bubble up untouched —
      // this component has no undo/redo of its own, and `home.tsx`'s global shortcut
      // listener is attached on `window`. React's synthetic `stopPropagation()` below also
      // stops the underlying native event, so without this early return, Ctrl+Z pressed
      // while editing never reached that listener at all: the toolbar buttons (wired
      // directly to `handleUndo`/`handleRedo`) worked, the keyboard shortcut silently did
      // nothing.
      if (isUndoRedoCombo(event)) return
      // Same for Ctrl/Cmd +/-/0/1/2: the window listener turns them into spread zoom and
      // preventDefaults them. Stopped here, Safari ran its own page zoom instead.
      if (isEditorZoomModifierShortcut(event)) return

      event.stopPropagation()

      // Chain caret hand-off at the frame's edges, mirroring `text-renderer.tsx`'s
      // interceptors one-for-one (WMPCM-9070). Each requires a COLLAPSED caret sitting
      // exactly at the boundary: without that, Backspace over a selection would hand off
      // instead of deleting the selection, and the arrows would collapse it the wrong way.
      const ta = event.currentTarget
      const isCollapsed = ta.selectionStart === ta.selectionEnd
      const atStart = isCollapsed && ta.selectionStart === 0
      const atEnd = isCollapsed && ta.selectionEnd === ta.value.length
      const isBarePress = !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey

      // Font size step: bare Up/Down over a REAL (non-collapsed) in-frame selection steps that
      // selection's point size by 1, instead of the browser's default caret move — mirrors the
      // whole-shape shortcut in `center-canvas/handlers/canvas-key-action-handlers.ts`, which only
      // ever fires OUTSIDE edit mode (its own `isEditable` guard excludes this textarea entirely).
      // A cross-frame selection reads as COLLAPSED here (see the comment below — it sits collapsed
      // at the anchor while the real range spans other frames), so `!isCollapsed` already excludes
      // that case without any extra check.
      if (isBarePress && !isCollapsed && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault()

        const currentSize = getEditingStyle()?.pointSize ?? DEFAULT_POINT_SIZE
        const rawNext = Math.round(currentSize) + (event.key === 'ArrowUp' ? 1 : -1)
        const clamped = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, rawNext))

        if (rawNext !== clamped) markPointSizeBoundaryHit()
        // Commit immediately rather than through the 500ms keystroke debounce: this is a
        // single discrete step, not a run of characters that should coalesce into one undo
        // entry, and without this Ctrl+Z right after the step found nothing on the stack yet.
        applyRunStyle({ pointSize: clamped }, { commitImmediately: true })
        // The selection itself doesn't move, so no native `selectionchange` fires from this
        // keypress — TypePanel's live re-sync (`editSelectionTick`) is driven entirely by
        // that event, so without this nudge the Font Size field only catches up whenever
        // something UNRELATED happens to re-render it. Synthetic, not real, but it's the same
        // event TypePanel already listens for.
        document.dispatchEvent(new Event('selectionchange'))

        return
      }

      // A live cross-frame selection is not in this textarea — it is collapsed at the
      // anchor while the real range spans several frames (WMPCM-9199). Backspace/Delete
      // would therefore eat one neighbouring character, and a printable key would insert
      // without removing anything. Both have to be routed to the owner, which replaces
      // every covered span at once.
      if (hasChainSelection && onChainSelectionReplace && !event.altKey) {
        const replacement = chainReplacementForKey(event)

        if (replacement != null) {
          event.preventDefault()
          onChainSelectionReplace(replacement)

          return
        }
      }

      // Shifted navigation across a link boundary (WMPCM-9199, widened by WMPCM-9200).
      // The bare-press interceptors below deliberately require `isBarePress`, so a shifted
      // key at the frame edge used to fall through to the textarea and stop there.
      //
      // Originally Left/Right only, on the grounds that a vertical extension had no
      // well-defined landing row in the next frame. It does: the x the caret leaves with,
      // against that frame's first or last row — which is what a browser does across a
      // column break, and what `rowEdgeOffsetNearestX` now resolves. Word, row and
      // whole-story units are handled for the same reason, since QA's repro lists
      // Shift+Ctrl/Cmd+Arrow and Shift+End alongside the plain arrows.
      const shiftNav = onChainSelectionExtend
        ? resolveShiftNavigation(event, getShortcutPlatform())
        : null

      if (shiftNav && onChainSelectionExtend) {
        const forward = shiftNav.direction === 1
        const focusLocal = forward ? ta.selectionEnd : ta.selectionStart
        const leavesFrame = shiftNavLeavesFrame({
          shiftNav,
          focusLocal,
          carets,
          selectionStart: ta.selectionStart,
          selectionEnd: ta.selectionEnd,
          valueLength: ta.value.length,
          chainFrameCount: chainFrameIds?.length ?? 0,
          hasNextInChain,
          hasPrevInChain,
        })

        // Once a cross-frame selection exists the textarea is collapsed at the anchor, so
        // its own offsets say nothing about where the focus is — EVERY shifted navigation
        // key has to be routed to the owner, not just the ones at a boundary. This is the
        // half QA saw as the selection "resetting" on Shift+End and Shift+Ctrl+Arrow.
        if (hasChainSelection || leavesFrame) {
          event.preventDefault()
          onChainSelectionExtend({
            frameId: shapeId,
            anchorLocal: forward ? ta.selectionStart : ta.selectionEnd,
            focusLocal,
            direction: shiftNav.direction,
            unit: shiftNav.unit,
          })

          return
        }
      }

      // An unshifted navigation key collapses a cross-frame selection, exactly as a click or a
      // printable key already does (WMPCM-6867). Nothing else on this path clears it — every
      // shifted case returned above, and this textarea's own range says nothing about a
      // selection that spans frames — so the highlight stayed painted across every member
      // while the caret moved out from under it. Newly reachable, because it takes a working
      // chain-wide Ctrl+A to reach that state at all.
      //
      // Deliberately NOT gated on `isBarePress`: that also excludes Ctrl/Cmd, and mod+Arrow
      // word-nav and mod+Home/End collapse a selection in every browser too. No
      // `preventDefault` — the textarea still owns where the caret lands.
      //
      // Falls through on purpose. An ArrowLeft whose caret already sits at offset 0 still
      // reaches the `onChainArrowLeftAtStart` hand-off below and continues into the previous
      // frame: that is the pre-existing collapsed-caret behaviour, merely made visible now
      // that a selection can precede it. Clearing here and returning would instead swallow
      // the key, which is the worse of the two.
      if (hasChainSelection && !event.shiftKey && CARET_NAVIGATION_KEYS.has(event.key)) {
        onChainSelectionChange?.(null)
      }

      if (
        isBarePress &&
        atStart &&
        hasPrevInChain &&
        onChainBackspaceAtStart &&
        event.key === 'Backspace'
      ) {
        event.preventDefault()
        onChainBackspaceAtStart()

        return
      }

      if (
        isBarePress &&
        atStart &&
        hasPrevInChain &&
        onChainArrowLeftAtStart &&
        event.key === 'ArrowLeft'
      ) {
        event.preventDefault()
        onChainArrowLeftAtStart()

        return
      }

      if (
        isBarePress &&
        atEnd &&
        hasNextInChain &&
        onChainArrowRightAtEnd &&
        event.key === 'ArrowRight'
      ) {
        event.preventDefault()
        onChainArrowRightAtEnd()

        return
      }

      if (matchesTextEditorShortcut(event, 'escape')) {
        event.preventDefault()
        onExitEdit(shapeId, { activateSelectTool: true })
      } else if (matchesTextEditorShortcut(event, 'selectAll')) {
        // Must preventDefault: `stopPropagation` above already keeps the canvas handler out,
        // but the browser's own textarea select-all would still run and `onSelect` would sync
        // it back, leaving just this shape's text selected (WMPCM-9070).
        event.preventDefault()
        // Chain first, then in-frame — same shape as `onChainClipboard` below, and for the
        // same reason. This branch used to be gated on `onSelectChain &&` and to discard the
        // result, which made Ctrl+A a DEAD KEY in two cases (WMPCM-9199): a solo frame fell
        // through to a native select-all that this `preventDefault()` had already killed,
        // and a chain member whose seed bailed (no uid, no chain, or fewer than two members
        // — see `seedChainTextSelectionOverFrames`) got nothing at all.
        if (!onSelectChain?.()) execSelectAll()
      } else if (matchesTextEditorShortcut(event, 'copy')) {
        event.preventDefault()
        // Cross-frame first — see `onChainClipboard`. The local `execCopy` cannot see a
        // selection that spans frames, and the collapsed textarea makes it a no-op.
        if (!onChainClipboard?.('copy')) execCopy()
      } else if (matchesTextEditorShortcut(event, 'cut')) {
        event.preventDefault()
        if (!onChainClipboard?.('cut')) execCut()
        // No `paste` branch here on purpose (WMPCM-9199). Intercepting mod+V and calling
        // `preventDefault()` suppressed the browser's own `paste` event, so `onPaste`
        // — the handler that actually preserves formatting — could never fire for the one
        // gesture users reach for most. Let the keystroke through; `onPaste` takes it.
      } else if (isModLetter(event, 'b')) {
        event.preventDefault()
        toggleBoldOrItalic('bold')
      } else if (isModLetter(event, 'i')) {
        event.preventDefault()
        toggleBoldOrItalic('italic')
      } else if (isModLetter(event, 'u')) {
        // Ctrl+U / Cmd+U: toggle underline
        event.preventDefault()
        applyRunStyle({ underline: !getEditingStyle()?.underline })
      } else if (event.key === 'Tab') {
        // Insert a literal tab instead of letting the browser move focus out of the
        // editor. execCommand routes through onChange → handleTextChange (undo-aware),
        // mirroring execCut's deletion path.
        event.preventDefault()
        if (!event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
          document.execCommand('insertText', false, '\t')
        }
      }
    },
    [
      applyRunStyle,
      carets,
      chainFrameIds,
      execCopy,
      execCut,
      execSelectAll,
      getEditingStyle,
      hasChainSelection,
      hasNextInChain,
      hasPrevInChain,
      markPointSizeBoundaryHit,
      onChainArrowLeftAtStart,
      onChainArrowRightAtEnd,
      onChainBackspaceAtStart,
      onChainClipboard,
      onChainSelectionChange,
      onChainSelectionExtend,
      onChainSelectionReplace,
      onExitEdit,
      onSelectChain,
      shapeId,
      toggleBoldOrItalic,
    ]
  )
}
